import React, { FC, ReactNode, useState } from 'react';
import { Cart, LineItem } from 'shared/types/cart';
import { Category, Variant } from 'shared/types/product';
import { ExtendedVariant } from 'shared/types/product/Variant';
import { Wishlist } from 'shared/types/wishlist';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import QuantitySelector from 'components/commercetools-ui/atoms/quantity-selector';
import Breadcrumb from 'components/commercetools-ui/molecules/breadcrumb';
import Gallery from 'components/commercetools-ui/organisms/gallery';
import { useAddToCartOverlay } from 'context/add-to-cart-overlay';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import { Reference } from 'types/reference';
import AdditionalInfo from './components/additional-info';
import ProductInformation from './components/product-information';
import { findMatchingLineItem } from './helpers/findMatchingLineItem';
import { useFinishAndSheens } from './hooks/useFinishAndSheens';
import useTrack from './hooks/useTrack';
import { UIProduct, UIProductContent } from './types';
import { FrontasticImage } from '../../../atoms/image';
import { ReviewListProps } from '../../review-list';

export interface ProductDetailsProps {
  product: UIProduct;
  productContent?: UIProductContent;
  variant: ExtendedVariant;
  url?: string;
  category?: Category;
  wishlist?: Wishlist;
  inModalVersion?: boolean;
  woodFinishHelper?: {
    text?: string;
    linkLabel?: string;
    link?: Reference;
  };
  wirebrushModal?: {
    title: string;
    text: string;
    images?: Array<{ name: string; image: FrontasticImage }>;
    closeButtonAriaLabel?: string;
  };
  onChangeVariant: (sku: string) => void;
  description?: ReactNode;
  plainTextDescription?: string;
  origin?: string;
  setIsOpen?: (value: boolean) => void;
  removeLineItem?: (item: LineItem) => Promise<void>;
  addToWishlist?: (lineItem: LineItem, count: number) => Promise<void>;
  onAddToCart?: (
    variant: Variant,
    quantity: number,
    finish?: string,
    sheen?: string,
    hasWirebrush?: boolean,
  ) => Promise<Cart>;
  inCartQuantity?: number;
  reviewListProps?: ReviewListProps;
}

const ProductDetails: FC<ProductDetailsProps> = ({
  product,
  productContent,
  variant,
  category,
  url,
  description,
  plainTextDescription,
  wishlist,
  inModalVersion,
  woodFinishHelper,
  wirebrushModal,
  onChangeVariant,
  setIsOpen,
  removeLineItem,
  addToWishlist,
  onAddToCart,
  reviewListProps,
  origin,
  // inCartQuantity = 0,
}) => {
  const { formatMessage } = useFormat({ name: 'cart' });

  const [quantity, setQuantity] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState(false);

  const { trackAddToCart } = useTrack({ product });

  const { finish, finishes, sheen, sheens, onChangeFinish, onChangeSheen } = useFinishAndSheens(product);

  const [hasWirebrush, setHasWirebrush] = useState<boolean>();
  const onChangeWirebrush = product.useWirebrush
    ? (isWirebrushChecked: boolean) => setHasWirebrush(isWirebrushChecked)
    : undefined;

  const { show } = useAddToCartOverlay();

  const handleAddToCart = async () => {
    if (quantity === 0) {
      return;
    }

    setLoading(true);

    try {
      const cart = await onAddToCart?.(variant, quantity, finish?.name, sheen, hasWirebrush);
      setLoading(false);
      setAdded(true);

      const matchingItem = cart
        ? findMatchingLineItem(cart, variant, {
            finish,
            sheen,
            hasWirebrush,
          })
        : null;

      // Show cart overlay with appropriate information
      if (matchingItem) {
        show(product, variant, quantity, {
          config: matchingItem.config,
          price: matchingItem.discountedPrice || matchingItem.price,
        });
      } else {
        show(product, variant, quantity);
      }

      setTimeout(() => {
        setAdded(false);
        setQuantity(1);
      }, 1000);

      if (cart) {
        trackAddToCart(cart);
      }
    } catch (error) {
      console.error('Error adding item to cart:', error);
      setLoading(false);
    }
  };

  const wrapperClassName = inModalVersion
    ? 'md:grid grid-cols-12 pt-70 pb-35 container'
    : 'pt-20 md:pt-24 pb-32 md:grid md:grid-cols-12 md:items-start container';

  const galleryContainerClassName = useClassNames([
    inModalVersion ? 'col-span-6' : 'md:col-span-7 lg:col-span-8',
    'md:pr-26 lg:pr-60',
    'md:mt-32',
  ]);

  const informationContainerClassName = useClassNames([
    inModalVersion ? 'col-span-6' : 'md:col-span-5 lg:col-span-4',
    'mt-24 md:mt-0',
    'md:mt-32',
  ]);

  return (
    <div className={wrapperClassName}>
      {category && !inModalVersion && (
        <Breadcrumb Separator="/" className="col-span-12 mb-16 w-fit md:mb-32">
          <Link
            key={category.categoryId}
            link={category._url}
            className="body-text-small text-primary-black hover:underline"
          >
            {formatMessage({ id: 'details.breadcrumb.prefix', defaultMessage: 'View' })} {category.name}
          </Link>
        </Breadcrumb>
      )}
      <div className={galleryContainerClassName}>
        <div className="relative inset-x-1/2 -mx-[50vw] w-screen md:static md:mx-0 md:w-auto">
          <Gallery
            images={product.images.length ? product.images : (variant.images?.map((img) => ({ src: img })) ?? [])}
            inModalVersion={inModalVersion}
          />
        </div>
      </div>
      <div className={informationContainerClassName}>
        <ProductInformation
          reviewListProps={reviewListProps}
          product={product}
          variant={variant}
          plainTextDescription={plainTextDescription}
          category={category}
          onChangeVariant={onChangeVariant}
          finish={finish}
          wirebrushModal={wirebrushModal}
          finishOptions={finishes?.map((finishObj) => `${finishObj.name}:${finishObj.hex}`)}
          sheen={sheen}
          sheenOptions={sheens}
          quantity={quantity}
          onChangeFinish={onChangeFinish}
          onChangeSheen={onChangeSheen}
          onChangeWirebrush={onChangeWirebrush}
          inModalVersion={inModalVersion}
          woodFinishHelper={woodFinishHelper}
          wishlist={wishlist}
          removeLineItem={removeLineItem}
          addToWishlist={addToWishlist}
          origin={origin}
        />

        <div className="mt-24 flex gap-16">
          {variant.price?.centAmount ? (
            <>
              {/* Since there's no tracking of stock, we use a really high value as the maximum */}

              <QuantitySelector value={quantity} minValue={1} maxValue={999} onChange={setQuantity} />
              <Button
                className="w-full rounded-sm text-14 font-medium"
                variant="primary"
                onClick={handleAddToCart}
                loading={loading}
                added={added}
                disabled={quantity === 0}
              >
                {formatMessage({ id: 'cart.add', defaultMessage: 'Add to cart' })}
              </Button>
            </>
          ) : (
            <div className="flex flex-col gap-y-8">
              <p className="body-text font-medium text-text-error">
                Product currently unavailable, please contact our Sales team.
              </p>
              <Link buttonVariant="secondary" className="flex justify-center" link={'/contact-us'} target="_blank">
                Contact Us
              </Link>
            </div>
          )}
        </div>

        {inModalVersion && (
          <Link
            link={url}
            className="mx-auto mt-28 block w-fit border-b border-transparent text-center text-14 leading-loose text-secondary-black hover:border-secondary-black"
            onClick={() => setIsOpen?.(false)}
          >
            {formatMessage({ id: 'more.details', defaultMessage: 'More details' })}
          </Link>
        )}
      </div>
      {!inModalVersion && <AdditionalInfo {...productContent} description={description} />}
    </div>
  );
};

export default ProductDetails;
